/* ============
 * Orders Index
 * ============
 *
 * This page shows the list of orders.
 */
import { mapGetters, mapMutations } from 'vuex';
import orderService from './../../../../services/order';
import invoiceService from './../../../../services/invoice';
import accountService from './../../../../services/account';
import store from './../../../../store';
import moment from 'moment';
import SearchMixin from './../../../../mixins/search';
import DateMixin from './../../../../mixins/date';
import debounce from 'debounce';
import bulkProcessOrderModal from './../../../../components/bulkProcessOrderModal/bulkProcessOrderModal.vue';

export default {
  data() {
    return {
      sortTypes: [
        {
          id: 'number|desc',
          name: this.$t('sort.newer'),
        },
        {
          id: 'created_at|asc',
          name: this.$t('sort.older'),
        },
        {
          id: 'last_week',
          name: this.$t('sort.lastWeek'),
        },
        {
          id: 'last_14days',
          name: this.$t('sort.last14Days'),
        },
        {
          id: 'last_21days',
          name: this.$t('sort.last21Days'),
        },
        {
          id: 'last_month',
          name: this.$t('sort.lastMonth'),
        },
      ],
      listTypes: [
        {
          id: 'all',
          name: this.$t('listType.all'),
        },
        {
          id: 'order',
          name: this.$t('listType.order'),
        },
        {
          id: 'edi',
          name: this.$t('listType.edi'),
        },
      ],
      loading: true,
      sort: 'number|desc',
      info: 'info',
      primary: 'primary',
      allType: 'all',
      search: '',
      multipleSelection: [],
      bulkSelectionLimit: 25,
      showAction: false,
      action: null,
      limit: 25,
    };
  },

  computed: {
    ...mapGetters({
      account: 'account',
      orders: 'paginatedOrders',
      view: 'getView',
      orderViewType: 'getOrderViewType',
      searchQuery: 'orderQuery',
    }),

    ordersListView() {
      return this.orders.items.map(c => ({
        name: `#${c.number}`,
        hashid: c.hashid,
        isEdi: c.orderType === 'EDI',
        ediOrderId: c.ediOrderId,
        type: (this.$t(`static.orders.types.${c.orderType.toLowerCase()}`)),
        status: c.sent ? this.$t('static.ediOrders.index.bill_sent') : (c.ediOrderId ? (this.$t(`static.ediOrders.index.${c.ediStatus.toLowerCase()}`)) : (this.$t(`static.orders.status.${c.orderStatus.toLowerCase()}`))),
        clientName: (c.orderType == 'EDI' ? `${c.clientName} ${c.ediStore}` : c.clientName),
        totalValue: typeof c.totalValue !== 'undefined' ? `${this.formatCurrencyAmount(c.totalValue)} kr` : '',
        totalQuantity: typeof c.totalValue !== 'undefined' ? `${c.totalQuantity} ${this.$t('static.orders.index.qty')}` : '',
        createdDate: `${moment(c.createdDate).format('DD-MM-YYYY')}`,
        orderProducts: c.orderProducts,
        id: c.id,
      }));
    },

    /**
     * Determines if the route is in edi order overview
     */
    isEdiOverview() {
      return this.$route.name === 'ediOrders.index';
    },

    /**
     * Determines if the route is in order overview
     */
    isOrderOverview() {
      return this.$route.name === 'orders.index';
    },

    ordersList() {
      return this.orders.items.map(o => (
        Object.assign({}, o, {
          hashnumber: o.orderStatus === 'cancelled' ?
            `#${o.number} (${this.$t('static.orders.index.cancelled')})` : `#${o.number}`,
          service: o.orderType === 'Appointment' && o.serviceName ? o.serviceName : '',
          value: typeof o.totalValue !== 'undefined' ? `${this.formatCurrencyAmount(o.totalValue)} kr` : '',
          amount: typeof o.totalValue !== 'undefined' ? `(${o.totalQuantity} ${this.$t('static.orders.index.qty')})` : '',
          start: o.orderType === 'Appointment' ? `${moment(o.date).format('DD/MM/YYYY')} ${o.time}` : false,
          orderStatus: (o.sent) ? 'bill_sent' : o.orderStatus || 'created',
          ediStatus: (o.sent && o.orderStatus !== 'cancelled') ? 'bill_sent' : o.ediStatus,
        })
      ));
    },

    /**
     * If the user has the EDI module.
     *
     * @return {Boolean}
     */
    hasEdiModule() {
      const modules = this.account.modules.map(m => m.name);
      return modules.indexOf('EDI') > -1;
    },

    /**
     * If the user has the Appointment module.
     *
     * @return {Boolean}
     */
    hasAppointmentModule() {
      const modules = this.account.modules.map(m => m.name);
      return modules.indexOf('Appointment') > -1;
    },

    selectedCount() {
      return this.multipleSelection.length || 0;
    },
  },

  mixins: [
    SearchMixin,
    DateMixin,
  ],

  components: {
    VCard: require('./../../../../components/card/card.vue'),
    VPagination: require('./../../../../components/paginationNew/paginationNew.vue'),
    VSelect: require('./../../../../components/form/select/select.vue'),
    VNoData: require('./../../../../components/noData/noData.vue'),
    VTable: require('./../../../../components/table/table.vue'),
  },

  mounted() {
    this.buildInnerSidebar();
    this.allType = this.orderViewType;
    this.all(1, 25);

    accountService.rememberPerPage('get', { from: 'order' })
      .then((data) => {
        this.limit = data.size;
        this.all(1, this.limit);
      }).catch((error) => {
        console.log(error);
      });

    if (this.view == 'card') {
      this.info = 'info';
      this.primary = 'primary';
    } else {
      this.primary = 'info';
      this.info = 'primary';
    }
  },
  destroyed() {
    this.unsetInnerSidebar();
    store.dispatch('resetOrders');
  },
  watch: {
    /**
     * Listem to router changes, used to check query params that have changed to apply filters.
     *
     * @param  {Object} route Current route object.
     */
    $route() {
      this.all(1, this.limit);
      this.multipleSelection = [];
    },

    multipleSelection(multipleSelection) {
      this.showAction = false;
      if (multipleSelection.length) {
        this.showAction = true;
      }
    },

    view(view) {
      this.showAction = false;
      if (view == 'card') {
        this.info = 'info';
        this.primary = 'primary';
      } else {
        this.primary = 'info';
        this.info = 'primary';
      }
    },
    orderViewType(orderViewType) {

    },
    loading(loading) {
      if (loading) {
        this.disableInnerSidebar();
      } else {
        this.enableInnerSidebar();
      }
    },
  },
  methods: {
    ...mapMutations({
      setInnerSidebar: 'SET_INNERSIDEBAR',
      unsetInnerSidebar: 'UNSET_INNERSIDEBAR',
      disableInnerSidebar: 'DISABLE_INNERSIDEBAR',
      enableInnerSidebar: 'ENABLE_INNERSIDEBAR',
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
    }),

    /**
     * Norwegian number format: comma decimals, space as thousands separator (matches order detail totals).
     */
    formatCurrencyAmount(value) {
      if (value === undefined || value === null || value === '') {
        return '';
      }
      const n = Number(value);
      if (Number.isNaN(n)) {
        return '';
      }
      return n.toLocaleString('nb-NO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    handleSelectionChange(val) {
      this.multipleSelection = val;
    },

    onSelectAction(value) {
      if (this.multipleSelection.length > this.bulkSelectionLimit) {
        const message = `${this.$t('static.orders.index.bulkMaxLimitExceededError')} ${this.bulkSelectionLimit}`;
        this.$message({
          showClose: true,
          message,
          customClass: 'error',
        });
        return;
      }
      if (value === 'dowloadDeliverySlip') {
        invoiceService.bulkDownloadDeliveryNote(this.multipleSelection.map(item => item.hashid));
        return;
      }
      if (value) {
        this.setModalContent(() => bulkProcessOrderModal);
        this.setModalProps({
          selectedOrders: this.multipleSelection,
          operation: value,
          onSubmit: this.handleBulkOrderActionSubmit,
        });
        this.setModalCloseOnClick(false);
        this.setModalShowClose(false);
        this.setModalCloseOnPressEscape(false);
        this.toggleModal(true);
      }
    },

    handleBulkOrderActionSubmit() {
      this.multipleSelection = [];
      this.all(1, this.limit);
    },

    changeView(view) {
      if (this.info != view) {
        this.primary = this.info;
        this.info = view;
        if (this.info == 'info') {
          store.dispatch('setView', 'card');
        } else {
          store.dispatch('setView', 'list');
        }
      }
    },

    perPage(size) {
      accountService.rememberPerPage('post', { from: 'order', perPage: size })
        .then((data) => {
          this.limit = data.size;
        }).catch((error) => {
          console.log(error);
        });
    },

    /**
     * Builds inner sidebar.
     */
    buildInnerSidebar() {
      const sidebar = [
        {
          title: this.$t('layouts.sidebar.orders'),
          options: [
            {
              title: this.$t('layouts.sidebar.products'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'Product',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.services'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'Service',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.wordpress'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'Wordpress',
                },
              },
            },
             /* {
                title: this.$t('layouts.sidebar.newOrders'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'created',
                  },
                },
              },
              {
                title: this.$t('layouts.sidebar.confirmed'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'confirmed',
                  },
                },
              },
              {
                title: this.$t('layouts.sidebar.inTransit'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'processed',
                  },
                },
              },
              {
                title: this.$t('layouts.sidebar.recieved'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'received',
                  },
                },
              },
              {
                title: this.$t('layouts.sidebar.invoiced'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'invoiced',
                  },
                },
              },
              {
                title: this.$t('layouts.sidebar.credited'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'credited',
                  },
                },
              },
              {
                title: this.$t('layouts.sidebar.cancelled'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'cancelled',
                  },
                },
              },
              {
                title: this.$t('layouts.sidebar.complete'),
                route: {
                  name: 'orders.index',
                  query: {
                    filter: 'completed',
                  },
                },
              },*/
            {
              title: this.$t('layouts.sidebar.all'),
              route: {
                name: 'orders.index',
              },
            },
          ],
          button: {
            title: `+ ${this.$t('static.orders.create.title')}`,
            route: {
              name: 'orders.create',
            },
          },
        },
      ];

      if (this.hasAppointmentModule) {
        sidebar.push({
          title: this.$t('layouts.sidebar.booking'),
          options: [
            {
              title: this.$t('layouts.sidebar.upcoming'),
              route: {
                name: 'appointments.index',
                query: {
                  appointment: 'upcoming',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.done'),
              route: {
                name: 'appointments.index',
                query: {
                  appointment: 'done',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.calendar'),
              route: {
                name: 'appointments.index',
                query: {
                  appointment: 'calendar',
                },
              },
            },
          ],
          button: {
            title: `+ ${this.$t('static.appointments.create.title')}`,
            route: {
              name: 'orders.create',
            },
          },
        });
      }

      if (this.hasEdiModule) {
        sidebar.push({
          title: this.$t('layouts.sidebar.orderFilter'),
          options: [
            {
              title: this.$t('layouts.sidebar.newOrders'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'created',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.outOfStock'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'out_of_stock',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.confirmed'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'confirmed',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.inTransit'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'processed',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.recieved'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'received',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.notInvoiced'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'notInvoiced',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.invoiced'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'invoiced',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.credited'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'credited',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.cancelled'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'cancelled',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.complete'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'completed',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.all'),
              route: {
                name: 'orders.index',
              },
            },
          ],
        });
      }

      /**
       * Order pickups sidebar
       */
      sidebar.push({
        title: this.$t('layouts.sidebar.orderPickups.title'),
        options: [
          {
            title: this.$t('layouts.sidebar.orderPickups.viewAll'),
            route: {
              name: 'orderPickups.index',
              query: {
                filter: 'all',
              },
            },
          },
        ],
      });


      this.setInnerSidebar(sidebar);
    },

    /**
     * Fetches paginated orders.
     *
     * @param  {int} page The page number.
     * @param  {int} limit The number of items displayed on a page.
     */
    all: debounce(function (page, limit) {
      store.dispatch('setOrderTypeView', this.allType);
      this.loading = true;
      const query = { page, limit, search: this.searchQuery.search };
      store.dispatch('setOrderQuery', query);
      this.search = this.searchQuery.search;
      const params = {
        page,
        limit,
        search: this.searchQuery.search,
        edi: this.$route.name === 'ediOrders.index',
        sort: this.sort,
        filter: this.$route.query.filter,
        listtype: this.allType,
      };
      orderService.items(params)
        .then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
    }, 800),

    onChangeSearch(name, value) {
      store.dispatch('setOrderQuery', { page: 1, limit: 25, search: value });
      this.all(this.searchQuery.page, 25);
    },
    /**
     * Delete specific order.
     *
     * @param  {Object} order The order object.
     */
    deleteOrder(order) {
      this.$confirm(this.$t('static.orders.index.confirmDelete'), this.$t('static.orders.index.confirmDeleteTitle'), {
        confirmButtonText: this.$t('button.delete'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        orderService.destroy(order.id);
      }).catch(() => { });
    },

    /**
     * Go to edit view for order.
     *
     * @param  {Object} order The order object.
     */
    editOrder(order) {
      this.$router.push({
        name: 'orders.edit',
        params: { id: order.id },
      });
    },

    /**
     * Go to edit view for order in new tab
     *
     * @param  {Object} order The order object.
     */
    editOrderNewTab(order) {
      const route = this.$router.resolve({
        name: 'orders.edit',
        params: { id: order.id },
      });
      window.open(route.href, '_blank');
    },
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/orders/index/index.js